<script lang="ts">
  import { config } from '#app/config'
  import { getTextDirection, activeLangs, regionify } from '#app/lib/languages/languages'
  import { setQuerystring } from '#app/lib/location'
  import { locationStore } from '#app/lib/location_store'
  import { metadataStore, prerenderStatusStore } from '#app/lib/metadata/update'
  import { objectEntries } from '#app/lib/utils'
  import type { AbsoluteUrl } from '#server/types/common'
  import { localLang } from '#user/lib/i18n'

  const { origin } = location
  const { instanceName, remoteEntitiesOrigin, orgFediverseAccount } = config

  $effect(() => {
    if (typeof $localLang === 'string') {
      document.documentElement.setAttribute('lang', $localLang)
      document.documentElement.setAttribute('dir', getTextDirection($localLang))
    }
  })

  const baseHref = $derived(`${origin}${$locationStore.route}` as AbsoluteUrl)

  const url: string = $derived($metadataStore?.url)
  const title: string = $derived($metadataStore?.title)
  const description: string = $derived($metadataStore?.description)
  const image: string = $derived($metadataStore?.image)
  const rss: string = $derived($metadataStore?.rss)
  const twitterCard: string = $derived($metadataStore?.['twitter:card'])
  const fediverseAcct: string = $derived($metadataStore?.fediverseAcct)

  const canonicalUrl = $derived(remoteEntitiesOrigin && url?.startsWith(`${origin}/entity/`) ? url.replace(origin, remoteEntitiesOrigin) : url)
  const fediverseCreator = $derived(fediverseAcct || orgFediverseAccount)

  const statusCode = $derived($prerenderStatusStore.statusCode)
  const header = $derived($prerenderStatusStore.header)
</script>

<svelte:head>
  {#if $metadataStore}
    <title>{title}</title>
    <link rel="canonical" href={canonicalUrl} />

    <!-- The default lang - en - doesnt need a lang querystring to be set.
  It could have one, but search engines need to know that the default url
  they got matches this languages hreflang -->
    <link rel="alternate" href={baseHref} hreflang="en" />
    {#each activeLangs as lang (lang)}
      {#if lang !== 'en'}
        <link rel="alternate" href={setQuerystring(baseHref, 'lang', lang)} hreflang={lang} />
      {/if}
    {/each}

    {#if typeof $localLang === 'string'}
      <meta property="og:locale" content={regionify[$localLang]} />
    {/if}

    {#each objectEntries(regionify) as [ lang, territory ] (lang)}
      {#if lang !== $localLang}
        <meta property="og:locale:alternate" content={territory} />
      {/if}
    {/each}

    <link rel="alternate" type="application/rss+xml" href={rss} />

    <meta name="twitter:title" content={title} />
    <meta name="twitter:card" content={twitterCard} />
    <meta name="twitter:description" content={description} />
    <meta name="twitter:image" content={image} />

    <meta property="og:site_name" content={instanceName} />
    <meta property="og:type" content="website" />
    <meta property="og:url" content={url} />
    <meta property="og:title" content={title} />
    <meta name="description" property="og:description" content={description} />
    <!-- limiting to one og:image to be sure it's the one selected. cf https://stackoverflow.com/questions/13424780/facebook-multiple-ogimage-tags-which-is-default -->
    <meta property="og:image" content={image} />

    {#if fediverseCreator}
      <meta name="fediverse:creator" content={fediverseCreator} />
    {/if}

  {/if}
  {#if statusCode}
    <meta name="prerender-status-code" content={statusCode.toString()} />
    {#if header}
      <meta name="prerender-header" content={header} />
    {/if}
  {/if}
</svelte:head>
